let r2ResponseStoragePromise = null;

async function getR2ResponseStorage() {
  if (!r2ResponseStoragePromise) {
    r2ResponseStoragePromise = import("localforage").then(({ default: localforage }) =>
      localforage.createInstance({
        name: "sharkfoto-r2-cache",
        storeName: "r2_responses",
      })
    );
  }

  return r2ResponseStoragePromise;
}

function normalizeUrl(url) {
  return typeof url === "string" ? url : String(url || "");
}

function getUrlPath(url) {
  const normalizedUrl = normalizeUrl(url);
  try {
    return new URL(normalizedUrl, "https://sharkfoto.local").pathname;
  } catch {
    return normalizedUrl.split("?")[0];
  }
}

function isR2CacheableUrl(url) {
  const path = getUrlPath(url);
  return (
    path.startsWith("/api/r2/") &&
    (path.endsWith(".json") || path.endsWith(".html"))
  );
}

function parseR2Response(url, text) {
  const path = getUrlPath(url);
  if (!path.endsWith(".json")) return text;
  if (!text) return null;
  return JSON.parse(text);
}

function buildFetchOptions(options, cached) {
  const headers = new Headers(options?.headers || {});

  if (cached?.etag && !headers.has("If-None-Match")) {
    headers.set("If-None-Match", cached.etag);
  }

  return {
    ...options,
    headers,
  };
}

function createFetchError(url, response) {
  const error = new Error(`R2 request failed: ${response.status} ${url}`);
  error.status = response.status;
  error.statusCode = response.status;
  return error;
}

export function useR2CachedFetch() {
  const requestFetch = useRequestFetch();

  return async function r2CachedFetch(url, options = {}) {
    const normalizedUrl = normalizeUrl(url);

    if (!import.meta.client || !isR2CacheableUrl(normalizedUrl)) {
      return requestFetch(url, options);
    }

    const cacheKey = normalizedUrl;
    const r2ResponseStorage = await getR2ResponseStorage();
    const cached = await r2ResponseStorage.getItem(cacheKey);
    let response;

    try {
      response = await fetch(normalizedUrl, buildFetchOptions(options, cached));
    } catch (error) {
      if (cached) return cached.data;
      throw error;
    }

    if (response.status === 304) {
      if (cached) return cached.data;
      throw createFetchError(normalizedUrl, response);
    }

    if (response.status >= 500 && cached) {
      return cached.data;
    }

    if (!response.ok) {
      throw createFetchError(normalizedUrl, response);
    }

    const text = await response.text();
    const data = parseR2Response(normalizedUrl, text);
    const etag = response.headers.get("ETag") || "";
    const contentType = response.headers.get("Content-Type") || "";

    await r2ResponseStorage.setItem(cacheKey, {
      url: normalizedUrl,
      etag,
      contentType,
      data,
      savedAt: Date.now(),
    });

    return data;
  };
}
